import { useAccountStore } from '@/modules/account'
import * as chatApi from '../../services/chatApi'
import type {
  ApplyFiltersPayload,
  ApplyFiltersSpec,
  ArtifactPayload,
  ContentBlock,
  MessageDeltaPayload,
  SseEvent,
  StepUsage,
  StepUsagePayload,
  TitleSetPayload,
  ToolCallFinishedPayload,
  ToolCallStartedPayload,
  ToolPlanPayload,
} from '../../types'
import { camelcaseSpec, isArtifactKind } from '../../utils/artifactKinds'
import type {
  ChatStoreCreator,
  Confirmation,
  DraftAssistantMessage,
  Getter,
  Setter,
  StreamMutations,
} from './types'

// Events that signal a turn is actively streaming (vs terminal or pure
// bookkeeping). When one of these arrives for the open session but our store
// has no draft, we just (re)mounted mid-turn and must resume the live view —
// see `resumeDraftIfMissing`.
//
// `agent_status` is deliberately NOT here. daystrom emits it from a concurrent
// heartbeat, so a final tick can race past `turn_complete`; if a content-free
// heartbeat could spin up a draft it would resurrect a finished turn with a
// spinner that never clears (no later terminal event to close it). It still
// drives the status label via `handleStreamEvent` — it just can't, on its own,
// create or revive a turn. A real content event does the resuming.
const LIVE_STREAM_EVENTS = new Set<SseEvent['name']>([
  'model_call_started',
  'model_call_finished',
  'message_delta',
  'tool_plan',
  'tool_call_started',
  'tool_call_finished',
  'tool_drafting',
  'artifact',
  'apply_filters',
  'step_usage',
])

// Compile-time exhaustiveness guard. Pass the discriminator after a switch
// to make TS complain when the backend adds an event we haven't handled.
function assertNever(value: never): never {
  throw new Error(`Unhandled chat-stream event: ${JSON.stringify(value)}`)
}

export const streamMutations: ChatStoreCreator<StreamMutations> = (
  set,
  get,
) => ({
  async sendMessage(
    slug: string,
    content: string,
    confirmation?: Confirmation,
  ) {
    const sessionId = get().currentSessionId
    if (sessionId === null) {
      throw new Error('No active chat session')
    }

    set({
      optimisticUserText: content,
      optimisticUserSessionId: sessionId,
      sendingMessage: true,
      thinkingSince: Date.now(),
      draftingTool: null,
      agentStatus: null,
      draftAssistant: emptyDraft(sessionId),
    })

    try {
      // The POST returns once lukita enqueues the streaming job (202). The
      // turn's events arrive over the session's ChatChannel and are applied
      // by `ingestStreamEvent`, which also finalizes (refresh + clear
      // sending) when the terminal event lands — so there is no finally
      // here and no post-POST refresh.
      await chatApi.sendMessage(slug, sessionId, content, {
        confirmation,
      })
    } catch (error) {
      // POST itself failed (credit gate, turn-in-progress, network). Nothing
      // is streaming, so roll the optimistic turn back entirely and surface
      // the error to the caller's toast handler.
      set({
        optimisticUserText: null,
        optimisticUserSessionId: null,
        draftAssistant: null,
        sendingMessage: false,
        thinkingSince: null,
        draftingTool: null,
      })
      throw error
    }
  },

  stopMessage(slug: string) {
    const sessionId = get().currentSessionId
    if (sessionId === null) return
    // Ask lukita to cancel the in-flight turn. The streaming job persists
    // the partial turn and emits a terminal event, which finalizes through
    // the channel; this gives the user instant local feedback meanwhile.
    void chatApi.stopTurn(slug, sessionId)
    set((state) => ({
      draftAssistant: cancelRunningToolCalls(state.draftAssistant),
      sendingMessage: false,
      thinkingSince: null,
      draftingTool: null,
      agentStatus: null,
    }))
  },

  ingestStreamEvent(slug: string, sessionId: string, event: SseEvent) {
    // Mark liveness so a load-time resume can tell a still-streaming turn
    // (frames arriving) from one that finished during the load gap (silent).
    set({ lastStreamEventAt: Date.now() })
    resumeDraftIfMissing(set, get, sessionId, event)
    if (event.name === 'title_set') {
      void persistServerTitle(slug, sessionId, event.data)
    }
    handleStreamEvent(set, event)
    if (event.name === 'turn_complete' || event.name === 'error') {
      void finalizeTurn(set, slug, sessionId)
    }
  },

  async reconcileInFlightTurn(slug: string, sessionId: string) {
    // The chat channel just reconnected after a drop. If a turn is still
    // marked in-flight for this session, it almost certainly finished
    // server-side during the gap (the turn runs to completion regardless of
    // client disconnect) and its terminal event was lost. Re-fetch the
    // persisted turn — exactly what a manual refresh did — so the spinner
    // clears and the answer appears. No-op if nothing is in flight or the
    // user has since switched sessions.
    if (!get().sendingMessage) return
    if (get().currentSessionId !== sessionId) return
    set({ thinkingSince: null, draftingTool: null, agentStatus: null })
    await finalizeTurn(set, slug, sessionId)
  },

  consumePendingFilters() {
    set({ pendingFilters: null })
  },

  seedMessage(message: string) {
    // Guard empties so a blank prompt can't be queued then silently dropped
    // by the `if (!seed) return` gate in useApplyPendingSeed.
    if (!message) return
    set({ pendingSeed: message })
  },

  claimPendingSeed() {
    const seed = get().pendingSeed
    if (seed !== null) set({ pendingSeed: null })
    return seed
  },
})

// Closes out a turn once its terminal event arrives over the channel: the
// streaming job has already persisted the (possibly partial) turn, so we
// fetch the authoritative messages, clear the draft, and refresh cost.
async function finalizeTurn(set: Setter, slug: string, sessionId: string) {
  // handleStreamEvent already cleared thinkingSince on the terminal event.
  // refreshAfterTurn no-ops the messages update if the user has since
  // switched sessions, but still clears the stale optimistic/draft state.
  await refreshAfterTurn(set, slug, sessionId).catch((error) => {
    set({ sendingMessage: false })
    throw error
  })
  // Cost is POSTed per model-call during the turn; refresh the sidebar bar.
  // The loader is throttled in the store, so back-to-back turns coalesce.
  void useAccountStore.getState().loaders.fetchAccountSummary()
}

// Navigating to another project (or refreshing) mid-turn unmounts the
// per-project store, so a fresh mount starts with no draft and
// sendingMessage=false even though the turn is still streaming server-side.
// The persisted user message is re-fetched, but incoming stream events would
// otherwise be dropped by the `if (!state.draftAssistant)` guards — leaving an
// empty chat that looks dead. When a live event arrives for the open session
// with no draft, spin one up so the streaming view + status line resume
// mid-turn. The authoritative full turn is still re-fetched on turn_complete.
function resumeDraftIfMissing(
  set: Setter,
  get: Getter,
  sessionId: string,
  event: SseEvent,
) {
  if (!LIVE_STREAM_EVENTS.has(event.name)) return
  const state = get()
  if (state.currentSessionId !== sessionId) return
  if (state.draftAssistant) return
  set({
    sendingMessage: true,
    thinkingSince: state.thinkingSince ?? Date.now(),
    draftAssistant: emptyDraft(sessionId),
    // Mark the optimistic session so `useChatSending` (gated on
    // optimisticUserSessionId === currentSessionId) reports the turn as live.
    // No optimisticUserText: the real user message comes from the fetched
    // messages, so setting it would double-render the question.
    optimisticUserSessionId: sessionId,
  })
}

export function emptyDraft(sessionId: string): DraftAssistantMessage {
  return {
    sessionId,
    contentBlocks: [],
    toolCalls: {},
    artifacts: {},
    stepUsages: [],
    stepBoundaries: [],
  }
}

// When the user hits Stop, the SSE stream is cut mid-flight and any tool
// calls still in `ok: null` would otherwise spin forever in the chip list.
// Two cases:
//
//   1. Pending (startedAt === null): announced by tool_plan but never
//      dispatched. We have nothing meaningful to show — no args, no result —
//      so we drop these entries entirely (both the toolCalls entry and the
//      orphan tool_use block from contentBlocks). The chip simply disappears.
//
//   2. Running (startedAt set, ok === null): mid-flight when the user
//      cancelled. We keep these and flip them to a terminal aborted status
//      so the row reads "(stopped)" in the chip with whatever args /
//      duration we already had.
function cancelRunningToolCalls(
  draft: DraftAssistantMessage | null,
): DraftAssistantMessage | null {
  if (!draft) return draft
  const now = Date.now()
  const droppedIds = new Set<string>()
  const nextCalls: typeof draft.toolCalls = {}
  let mutated = false
  for (const [id, call] of Object.entries(draft.toolCalls)) {
    if (call.ok === null && call.startedAt == null) {
      // Pending — drop entirely; nothing meaningful to surface.
      droppedIds.add(id)
      mutated = true
      continue
    }
    if (call.ok === null) {
      mutated = true
      nextCalls[id] = {
        ...call,
        ok: false,
        errorCode: 'aborted',
        finishedAt: now,
      }
    } else {
      nextCalls[id] = call
    }
  }
  if (!mutated) return draft
  const nextBlocks =
    droppedIds.size > 0
      ? draft.contentBlocks.filter(
          (block) => !(block.type === 'tool_use' && droppedIds.has(block.id)),
        )
      : draft.contentBlocks
  return { ...draft, toolCalls: nextCalls, contentBlocks: nextBlocks }
}

async function persistServerTitle(
  slug: string,
  sessionId: string,
  data: unknown,
): Promise<void> {
  const raw = data as Record<string, unknown> | null
  const title = raw && typeof raw.title === 'string' ? raw.title.trim() : ''
  if (!title) return
  try {
    await chatApi.renameSession(slug, sessionId, title)
  } catch {
    // Title is cosmetic — if persistence fails the optimistic local
    // update still stands for the current session, and the next fetch
    // will reconcile.
  }
}

async function refreshAfterTurn(set: Setter, slug: string, sessionId: string) {
  const session = await chatApi.fetchSession(slug, sessionId)
  set((state) => {
    if (state.currentSessionId !== sessionId) {
      // The user may have started a turn in the other chat; leave it alone.
      const ownedByThisTurn =
        state.draftAssistant?.sessionId === sessionId ||
        state.optimisticUserSessionId === sessionId
      if (!ownedByThisTurn) return {}
      return {
        sendingMessage: false,
        optimisticUserText: null,
        optimisticUserSessionId: null,
        draftAssistant: null,
      }
    }
    // Cleared with the new messages so no card re-enables before they land.
    return {
      sendingMessage: false,
      messages: session.messages,
      cancelledReportEditTokens: session.cancelledReportEditTokens ?? [],
      optimisticUserText: null,
      optimisticUserSessionId: null,
      draftAssistant: null,
    }
  })
}

/**
 * @internal
 *
 * Exported ONLY so unit tests can feed synthetic events without going
 * through the channel. Do not import from production code — channel
 * events must go through `streamMutations.ingestStreamEvent`, which owns
 * the `title_set` persist side-effect and the terminal-event finalization
 * (clear sending + fetch the persisted turn + refresh cost). Bypassing it
 * desynchronises the store with the server.
 */
export function handleStreamEvent(set: Setter, event: SseEvent) {
  // `event` is a discriminated union from chat-protocol.ts; switching on
  // `event.name` narrows `event.data` to the matching payload, and TS
  // flags any new event the backend adds that this switch doesn't cover.
  switch (event.name) {
    case 'message_delta':
      applyMessageDelta(set, event.data)
      break
    case 'tool_plan':
      applyToolPlan(set, event.data)
      break
    case 'tool_drafting':
      // The model is still WRITING this tool call's arguments (e.g. a
      // report-section rewrite); no text streams during that, so this is
      // the only liveness signal.
      set({ draftingTool: { name: event.data.name, chars: event.data.chars } })
      break
    case 'agent_status':
      // Semantic activity signal; ThinkingStatus turns it into playful copy.
      // Re-emitted as a heartbeat by daystrom during quiet stretches, so this
      // also keeps the status line alive through a subagent's silent run.
      set({
        agentStatus: {
          activity: event.data.activity,
          subagent: event.data.subagent ?? null,
          detail: event.data.detail ?? null,
        },
      })
      break
    case 'tool_call_started':
      applyToolCallStarted(set, event.data)
      break
    case 'tool_call_finished':
      applyToolCallFinished(set, event.data)
      break
    case 'artifact':
      applyArtifact(set, event.data)
      break
    case 'apply_filters':
      applyFilters(set, event.data)
      break
    case 'step_usage':
      applyStepUsage(set, event.data)
      break
    case 'model_call_started':
      applyModelCallStarted(set)
      break
    case 'model_call_finished':
      // The drafting status only makes sense while its model call runs.
      set({ draftingTool: null })
      break
    case 'persist':
    case 'summary_updated':
      // Persist + summary_updated are server-side bookkeeping signals we
      // already react to via the post-stream session refresh, not here.
      break
    case 'subagent_started':
    case 'subagent_finished':
      // TODO: real rendering for delegated subagent runs. For now we
      // acknowledge the boundary markers so the exhaustiveness check
      // doesn't throw, and let the subagent's interior events (the
      // message_delta / tool_call_* / step_usage stream the server
      // forwards between these markers) flow into the same draft.
      // Visually distinguishing them lands in a follow-up.
      break
    case 'title_set':
      applyServerTitle(set, event.data)
      break
    case 'turn_complete':
    case 'error':
      // Stop the thinking timer. The rest of finalization (clear sending +
      // fetch the persisted turn) is driven by `ingestStreamEvent`, which
      // owns the slug/sessionId context the refresh needs.
      set({ thinkingSince: null, draftingTool: null, agentStatus: null })
      break
    default:
      assertNever(event)
  }
}

// Optimistic local update only. The persist-to-lukita side-effect happens
// in `ingestStreamEvent`, where `slug` is in scope.
function applyServerTitle(set: Setter, data: TitleSetPayload) {
  const title = data.title.trim()
  if (!title) return
  set((state) => {
    const sessionId = state.currentSessionId
    if (!sessionId) return {}
    const nextSessions = state.sessions.map((session) => {
      if (session.id !== sessionId) return session
      // A user-set title always wins over the server-generated one.
      if (session.title && session.title.trim() !== '') return session
      return { ...session, title }
    })
    if (nextSessions === state.sessions) return {}
    return { sessions: nextSessions }
  })
}

function applyModelCallStarted(set: Setter) {
  set((state) => {
    // Preserve the original turn-start time so the timer counts continuously
    // across every model step + tool call until the turn completes.
    const thinkingSince = state.thinkingSince ?? Date.now()
    const draft = state.draftAssistant
    if (!draft) return { thinkingSince }
    return {
      thinkingSince,
      draftAssistant: {
        ...draft,
        stepBoundaries: [...draft.stepBoundaries, draft.contentBlocks.length],
      },
    }
  })
}

function applyArtifact(set: Setter, data: ArtifactPayload) {
  // An artifact kind this build doesn't know (e.g. a newer daystrom) is
  // dropped rather than crashing the reducer; the persisted-message fetch
  // after the turn applies the same filter.
  const kind = data.kind
  if (!isArtifactKind(kind)) return
  set((state) => {
    if (!state.draftAssistant) return {}
    return {
      draftAssistant: {
        ...state.draftAssistant,
        artifacts: {
          ...state.draftAssistant.artifacts,
          [data.tool_use_id]: {
            toolUseId: data.tool_use_id,
            artifactId: data.artifact_id,
            kind,
            spec: camelcaseSpec(kind, data.spec),
          },
        },
      },
    }
  })
}

function applyFilters(set: Setter, data: ApplyFiltersPayload) {
  // The wire shape declares spec as a generic Record; the actual contents
  // follow the apply_filters tool's spec contract (see filters.py).
  const spec = (data.spec ?? {}) as unknown as ApplyFiltersSpec
  set({
    pendingFilters: {
      filters: spec.filters ?? [],
      clear: Boolean(spec.clear),
    },
  })
}

function applyMessageDelta(set: Setter, data: MessageDeltaPayload) {
  set((state) => {
    if (!state.draftAssistant) return {}
    const blocks = [...state.draftAssistant.contentBlocks]
    const last = blocks[blocks.length - 1]
    if (last && last.type === 'text') {
      blocks[blocks.length - 1] = { type: 'text', text: last.text + data.text }
    } else {
      blocks.push({ type: 'text', text: data.text })
    }
    return {
      draftAssistant: { ...state.draftAssistant, contentBlocks: blocks },
    }
  })
}

// Pre-announce the batch of tool calls about to run. The model has finished
// its turn but the dispatcher hasn't fired any tool yet — we paint each call
// as "pending" so the user sees the upfront checklist. Each subsequent
// tool_call_started transitions the matching entry pending → running, and
// each tool_call_finished transitions it to ok / error. The existing
// renderer walks contentBlocks + decorates with toolCalls status, so once
// the blocks are in place the UI updates fall out for free.
function applyToolPlan(set: Setter, data: ToolPlanPayload) {
  set((state) => {
    if (!state.draftAssistant) return {}
    const existingIds = new Set(
      state.draftAssistant.contentBlocks
        .filter(
          (block): block is ContentBlock & { type: 'tool_use' } =>
            block.type === 'tool_use',
        )
        .map((block) => block.id),
    )
    const newBlocks: ContentBlock[] = []
    const newCalls = { ...state.draftAssistant.toolCalls }
    // Same step-attribution rule as applyToolCallStarted: step_usage for
    // step N is emitted before tool_call events for step N's tools, so
    // the latest stepUsages index is the step these calls belong to.
    const stepIndex = Math.max(0, state.draftAssistant.stepUsages.length - 1)
    for (const call of data.calls) {
      if (existingIds.has(call.tool_use_id)) continue
      newBlocks.push({
        type: 'tool_use',
        id: call.tool_use_id,
        name: call.name,
        input: {},
      })
      newCalls[call.tool_use_id] = {
        id: call.tool_use_id,
        name: call.name,
        ok: null,
        startedAt: null,
        stepIndex,
      }
    }
    if (newBlocks.length === 0) return {}
    return {
      draftAssistant: {
        ...state.draftAssistant,
        contentBlocks: [...state.draftAssistant.contentBlocks, ...newBlocks],
        toolCalls: newCalls,
      },
    }
  })
}

function applyToolCallStarted(set: Setter, data: ToolCallStartedPayload) {
  set((state) => {
    if (!state.draftAssistant) return {}
    // If the call was already announced by tool_plan, the block is already
    // in contentBlocks — patch its `input` in place (tool_plan only carries
    // names; tool_call_started has the real args). We deliberately do NOT
    // overwrite `name`: if the server somehow announces and dispatches
    // different names for the same id (rename mid-stream, replay path,
    // backend bug), the planned name is the user's contract — keeping it
    // surfaces a future divergence at code-review time instead of silently
    // rebranding the chip on the user.
    const existingIndex = state.draftAssistant.contentBlocks.findIndex(
      (block) => block.type === 'tool_use' && block.id === data.id,
    )
    let blocks: ContentBlock[]
    if (existingIndex >= 0) {
      blocks = state.draftAssistant.contentBlocks.map((block, index) => {
        if (index !== existingIndex || block.type !== 'tool_use') return block
        return { ...block, input: data.args }
      })
    } else {
      blocks = [
        ...state.draftAssistant.contentBlocks,
        { type: 'tool_use', id: data.id, name: data.name, input: data.args },
      ]
    }
    // step_usage for step N is emitted *before* the tool_call_started events
    // for step N's tools, so the most recent index in stepUsages is the step
    // this call belongs to. Applies identically whether or not tool_plan
    // already announced this id — we don't inherit the planned stepIndex
    // because the two rules must stay in lock-step.
    const stepIndex = Math.max(0, state.draftAssistant.stepUsages.length - 1)
    const previous = state.draftAssistant.toolCalls[data.id]
    const toolCalls = {
      ...state.draftAssistant.toolCalls,
      [data.id]: {
        id: data.id,
        // Preserve the planned name when the id was pre-announced.
        name: previous?.name ?? data.name,
        ok: null,
        startedAt: Date.now(),
        stepIndex,
      },
    }
    return {
      draftAssistant: {
        ...state.draftAssistant,
        contentBlocks: blocks,
        toolCalls,
      },
    }
  })
}

function applyToolCallFinished(set: Setter, data: ToolCallFinishedPayload) {
  set((state) => {
    if (!state.draftAssistant) return {}
    const previous = state.draftAssistant.toolCalls[data.id]
    const toolCalls = {
      ...state.draftAssistant.toolCalls,
      [data.id]: {
        ...previous,
        id: data.id,
        name: data.name,
        ok: data.ok,
        errorCode: data.error_code ?? null,
        startedAt: previous?.startedAt ?? null,
        finishedAt: Date.now(),
      },
    }
    return {
      draftAssistant: { ...state.draftAssistant, toolCalls },
    }
  })
}

function applyStepUsage(set: Setter, data: StepUsagePayload) {
  const usage = data.usage ?? {}
  const next: StepUsage = {
    step: data.step,
    model: data.model ?? null,
    stopReason: data.stop_reason ?? null,
    inputTokens: usage.input_tokens ?? 0,
    outputTokens: usage.output_tokens ?? 0,
    cacheReadInputTokens: usage.cache_read_input_tokens ?? null,
    cacheCreationInputTokens: usage.cache_creation_input_tokens ?? null,
    costMicros: data.cost_micros ?? 0,
  }
  set((state) => {
    if (!state.draftAssistant) return {}
    return {
      draftAssistant: {
        ...state.draftAssistant,
        stepUsages: [...state.draftAssistant.stepUsages, next],
      },
    }
  })
}
